import { Box } from '@mui/system';
import { useTranslation } from 'react-i18next';
import { Link, Button, Checkbox, Grid, Step, StepLabel, Stepper, Typography } from '@mui/material';
import { useCallback, useRef, useState } from 'react';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { InvoiceSendingTypeIntegerEnum } from 'src/shared/models/enums/invoiceSendingTypeInteger.enum';
import { InvoiceMissionResponse } from 'src/shared/models/invoiceMission/invoiceMission';
import { KivraLookupSearchFilter } from 'src/shared/models/kivra/kivraLookupSearchfilter';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { MultiStepIcon } from 'src/shared/components/multistepper/multiStepIcon';
import { useMultiStepForm } from 'src/shared/hooks/useMultiStepForm';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { ArrowBackIos } from '@mui/icons-material';
import { StyledMultiStepConnector } from 'src/shared/components/multistepper/styledMultiStepConnector';
import { SendMemberInvoiceKivra } from './sendMemberInvoiceKivra';
import { InvoiceMissionKivraMembersDataGrid } from './invoiceMissionKivraMembersDataGrid';

type MemberInvoiceKivraProps = {
  modalOpen: boolean;
  onSave: () => void;
  onClose: () => void;
  invoiceMission: InvoiceMissionResponse;
  hideBackdrop?: boolean;
  sendingType: InvoiceSendingTypeIntegerEnum;
};

export const MemberInvoiceKivra = (props: MemberInvoiceKivraProps) => {
  const { modalOpen, onSave, onClose, invoiceMission, hideBackdrop = false, sendingType } = props;
  const { t } = useTranslation();
  const modalTop = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [serverErrors] = useState<string[]>([]);
  const [priceListConfirmed, setPriceListConfirmed] = useState<boolean>(false);
  const [totalKivraMembers, setTotalKivraMembers] = useState<number>(0);
  const [kivraMembersFilter, setKivraMembersFilter] = useState<KivraLookupSearchFilter>({
    sortCol: 'firstName',
    sortDir: 'asc',
    pageIndex: 0,
    pageSize: 25,
  });

  // Ordningen ska vara samma som i useMultiStepForm
  const stepOneInvalid = totalKivraMembers === 0;
  const stepTwoInvalid = totalKivraMembers === 0;
  const isStepComplete = [!stepOneInvalid, !stepTwoInvalid];

  const allStepsComplete = isStepComplete.every((v) => v === null || v);

  const linkToPriceList = 'https://www.foreningssupport.se/priser';

  const handleLoadingChange = useCallback((val: boolean) => {
    setLoading(val);
  }, []);

  // Steg i formuläret
  const { step, steps, currentStepIndex, isFirstStep, isLastStep, back, next, goTo } = useMultiStepForm([
    <InvoiceMissionKivraMembersDataGrid
      invoiceMissionId={invoiceMission.invoiceMission_id}
      sendingtype={sendingType}
      kivraMembersCallback={(val) => {
        setTotalKivraMembers(val);
      }}
      searchFilter={kivraMembersFilter}
      searchFilterCallback={(newFilter) =>
        setKivraMembersFilter((prev) => ({
          ...prev,
          ...newFilter,
        }))
      }
      loadingCallback={handleLoadingChange}
    />,
    <SendMemberInvoiceKivra
      invoiceMission={invoiceMission}
      sendingType={sendingType}
      totalKivraMembers={totalKivraMembers}
      onClose={() => {
        goTo(0);
        onClose();
      }}
      navigateToInvoiceMission
      loadingCallback={handleLoadingChange}
    />,
  ]);

  const onHandleClose = useCallback(() => {
    goTo(0);
    onClose();
  }, [goTo, onClose]);

  return (
    <GenericDialog
      open={modalOpen}
      padding={0}
      disableBackdropClosing
      dialogTitle={t('invoice:invoiceMissionPage.kivra.modalHeader')}
      onClose={onHandleClose}
      onSave={onSave}
      showDialogActions
      hideBackdrop={hideBackdrop}
      dialogActions={
        <Grid
          container
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {isLastStep && currentStepIndex === 1 && (
            <Grid item xs={12} sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', alignItems: 'center', mb: 1 }}>
              <Checkbox checked={priceListConfirmed} onChange={(e) => setPriceListConfirmed(e.target.checked)} />
              <Typography>
                {t('invoice:invoiceMissionPage.kivra.confirmationCheckbox')}
                <Link href={linkToPriceList} target='_blank' rel='noopener noreferrer' color='primary' underline='hover'>
                  {t('invoice:invoiceMissionPage.kivra.priceList')}
                </Link>
                .
              </Typography>
            </Grid>
          )}
          {!isFirstStep ? (
            <Button
              type='button'
              variant='outlined'
              onClick={() => {
                back();
              }}
              disabled={loading}
              startIcon={<ArrowBackIos fontSize='small' />}
            >
              {t('common:button.back')}
            </Button>
          ) : (
            <Box />
          )}
          {!isLastStep && currentStepIndex === 0 && (
            <Button onClick={next} disabled={loading || stepOneInvalid} disableElevation variant='contained'>
              {t('common:button.continue')}
            </Button>
          )}
          {isLastStep && currentStepIndex === 1 && (
            <>
              <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
                <Checkbox checked={priceListConfirmed} onChange={(e) => setPriceListConfirmed(e.target.checked)} />
                <Typography>
                  {t('invoice:invoiceMissionPage.kivra.confirmationCheckbox')}
                  <Link href={linkToPriceList} target='_blank' rel='noopener noreferrer' color='primary' underline='hover'>
                    {t('invoice:invoiceMissionPage.kivra.priceList')}
                  </Link>
                  .
                </Typography>
              </Box>
              <Button
                type='submit'
                form='invoiceForm'
                disabled={loading || !allStepsComplete || !priceListConfirmed}
                disableElevation
                variant='contained'
              >
                {t('invoice:createMemberInvoice.sendInvoices')}
              </Button>
            </>
          )}
        </Grid>
      }
      dialogContent={
        <Box
          ref={modalTop}
          sx={{
            position: 'relative',
            border: (theme) => (serverErrors.length > 0 ? `8px solid ${theme.palette.error.main}` : 'none'),
            minHeight: '22rem',
          }}
        >
          <LoadingOverlay loading={loading} />
          <Stepper activeStep={currentStepIndex} alternativeLabel sx={{ mb: 2, mt: 3 }} connector={<StyledMultiStepConnector />}>
            {steps.map((_item, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <Step key={i}>
                <StepLabel StepIconComponent={(stepProps) => <MultiStepIcon {...stepProps} isDisabled isComplete={isStepComplete[i]} />}>
                  {i + 1}. {t(`invoice:invoiceMissionPage:stepLabelsKivra.step${i}`)}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
          <GridWrapper>
            <Grid item xs={12} mt={1.5}>
              {step}
            </Grid>
          </GridWrapper>
        </Box>
      }
    />
  );
};
